import FetchHelper from '../helpers/fetch_helper';
import Actions from '../actions/actions';
import StepsHelper from '../helpers/financing_request/steps_helper';

const CustomizeDispatcher = {
  async fetchEnergyDetails(customerId) {
    try {
      const energyDetailsResponse = await FetchHelper.fetchJson(`customers/${customerId}/energy-details`, {
        method: 'GET',
      });
      const energyDetails = energyDetailsResponse.results;
      this.setStoreVal('energyDetails', energyDetails);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to fetch energy details' },
      });
    }
  },

  setAmountFinancedZeroValidation(bool) {
    this.setStoreVal('financingMethodZeroValidation', bool);
  },

  getAmountFinancedZeroValidation() {
    return this.getStoreVal('financingMethodZeroValidation');
  },

  setAmountFinancedOverridden(bool) {
    this.mergeStoreVal('financingMethod', { amountFinancedOverridden: bool });
  },

  async updateFinancingMethod({ id, proposalId, customerId, body }) {
    let response;
    this.incrementLock('updateFinancingMethod');
    try {
      if (customerId) {
        response = await FetchHelper.fetchJson(`customers/${customerId}/financing-methods`, {
          method: 'PATCH',
          version: '/v3',
          body,
        });
      } else {
        response = await FetchHelper.fetchJson(`proposals/${proposalId}/financing-methods/${id}`, {
          method: 'PUT',
          body,
        });
      }

      this.dispatch({ type: 'setFinancingMethod', data: response.results?.find((e) => e.isPrimary) });
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Invalid financing method' },
      });
    }

    // with VOI isRequired coming from the backend, if we're past credit check, we need to refetch eligibility after an update to amt financed
    if (this.getStoreVal('eligibility')) {
      await this.dispatch({ type: 'fetchEligibility', data: { customerId: this.getStoreVal('customer').id } });
    }

    this.decrementLock('updateFinancingMethod');
    return response;
  },

  async changeOrder({ term, amountFinanced, apr, loanType, deferralMonths, productType, suppressEmail }) {
    let proposalId = this.getStoreVal('proposal').id;
    // let financingMethodId = this.getStoreVal('financingMethod').id;

    let customerId = this.getStoreVal('customer').id;

    let oldFRID = this.getStoreVal('financingRequest');

    await Actions.endPollFinancingRequest();
    await Actions.endPollEligibility();

    let response;
    this.incrementLock('updateFinancingMethod');

    Actions.generatingDocs(true);
    Actions.setRoute(`/proposal/${proposalId}/generating-documents`);
    Actions.setView({ page: 'generatingDocuments' });

    try {
      response = await FetchHelper.fetchJson(`customers/${customerId}/adjust-financing`, {
        method: 'POST',
        version: '/v3',
        body: { term, amountFinanced, apr, loanType, productType, deferralMonths, suppressEmail },
      });
      this.dispatch({ type: 'setFinancingMethod', data: response.results?.find((e) => e.isPrimary) });

      let proposal = await CustomizeDispatcher.tryTillSucceed(
        async () => Actions.fetchProposal(proposalId),
        async (proposal) => (proposal.financingRequestId ? Promise.resolve(proposal) : Promise.reject(proposal)),
        6, //tries
        5000 // time between tries (multiplied)
      );

      let financingRequestIdNew = proposal.financingRequestId;

      if (!proposal.financingRequestId) {
        Actions.setErrorMessages(['Financing Request did not complete in allotted time.']);
        return;
      }

      await Actions.fetchFinancingRequest({
        financingRequestId: financingRequestIdNew,
        waitOnGeneratedDocs: true,
      });

      if (StepsHelper.hasHadDocsUploadedOrSentForSignature(oldFRID)) {
        // wait for docs to have been sent out for signature
        try {
          await CustomizeDispatcher.tryTillSucceed(
            async () => Actions.fetchFinancingRequest({ financingRequestId: financingRequestIdNew }),
            async (frid) =>
              frid.steps[0].docs.every(
                ({ currentRevision }) =>
                  !!currentRevision?.currentEnvelopeId ? Promise.resolve(proposal) : Promise.reject(proposal),
                6,
                5000 // last wait will be 30 seconds
              )
          );
        } catch (e) {}
      }
      await Actions.goToProposalPage({
        subPage: 'step-documents',
        proposalId: proposalId,
        financingRequestId: financingRequestIdNew,
      });

      await Actions.generatingDocs(false);

      await Actions.beginPollEligibilitySlow({ customerId });

      setTimeout(() => {
        // Actions.fetchFinancingRequest({financin})
      }, 10000);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Invalid financing method' },
      });
      this.dispatch({ type: 'setFailWhaleMessages', data: error });
    }
    this.decrementLock('updateFinancingMethod');
    return response;
  },

  async adjustCustomer({ customerId, cphone1, cemail, csecondaryemail, csecondaryphone }) {
    const response = await FetchHelper.fetchJson(`customers/${customerId}/adjust-customer`, {
      method: 'POST',
      version: '/v3',
      body: { cphone1, cemail, csecondaryemail, csecondaryphone },
    });

    this.dispatch({ type: 'setCustomer', data: response.results[0] });
  },

  setEsignIframeSrc(data) {
    this.setStoreVal('esignSrc', data);
  },

  /**
   * Attempts to use operationFunction till it returns a non Reject result
   * Runs ValidationFunction on non rejectResult to see if it rejects it, if reject go back to hitting operationFunction
   * else drop thru with resolved result
   * after each iteration wait 3000 ms * iteration counter
   * @param operationFunction
   * @param validateFunction async function to validate return promise.resolve or promise.reject
   * @param maxTries defaults to 6
   * @param timeBetween defaults to 3000
   * @returns {Promise<any>}
   */
  async tryTillSucceed(operationFunction, validateFunction, maxTries = 6, timeBetween = 3000) {
    return new Array(maxTries).fill(Promise.reject()).reduce((acc, v, i) => {
      return acc.catch(() => {
        return operationFunction()
          .then((result) => validateFunction(result))
          .catch(
            (err) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  reject(err);
                }, timeBetween * (i + 1));
              })
          );
      });
    }, Promise.reject());
  },

  saveFinancingMethodUpdates(financingMethodUpdates) {
    if (financingMethodUpdates !== null && financingMethodUpdates !== undefined) {
      this.setStoreVal('financingMethodUpdates', null);
      this.setStoreVal('financingMethodUpdates', financingMethodUpdates);
    } else {
      this.setStoreVal('financingMethodUpdates', null);
    }
  },
};

export default CustomizeDispatcher;
